import { useChat } from '@ai-sdk/react';
import { useStore } from '@nanostores/react';
import { type ChatRequestOptions } from 'ai';
import isEqual from 'fast-deep-equal';
import { delay } from 'framer-motion';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { toast } from 'react-toastify';
import { onFileStream } from './on-file-stream';
import type { PreviewRef } from '~/components/workbench/Preview';
import { clientFetch } from '~/lib/.client/fetch';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { sendAppSignalError } from '~/lib/appsignal.client';
import { chatHooks } from '~/lib/chat-hooks';
import { isGithubBranchSwitch } from '~/lib/client-github';
import { BOLT_ATTACHMENTS_PREFIX, BOLT_SUPABASE_CONNECT_MSG_PREFIX } from '~/lib/constants';
import { errorHandler, trackResolvedErrors } from '~/lib/error-handling';
import { ChatError, ClientError } from '~/lib/errors';
import { useUserConfirmationFlow } from '~/lib/hooks/useConfirmation';
import { fromDBConfirmationInfoToChatMessage } from '~/lib/hooks/useMessages/db-change';
import { prepareRequestBody } from '~/lib/hooks/useMessages/prepare-body';
import { getChatHistory } from '~/lib/persistence/chats';
import { ImportTemplateAction, InstallAction } from '~/lib/runtime/actions';
import { StreamingDiffParser } from '~/lib/runtime/diff-parser';
import { StreamingMessageParser, type ActionCallbackData } from '~/lib/runtime/message-parser';
import { chatStore } from '~/lib/stores/chat';
import { projectStore } from '~/lib/stores/project';
import { supabaseStore } from '~/lib/stores/supabase';
import { templateStore } from '~/lib/stores/template';
import { userStore } from '~/lib/stores/user';
import { versionHistoryStore } from '~/lib/stores/version-history/version-history';
import { workbenchStore } from '~/lib/stores/workbench';
import type { APITokenStatsResponse } from '~/routes/api.token-stats';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { type Message, type SupabaseDBChangeMessage } from '~/types/chat';
import { NO_DEPLOYMENT_STATUS_MESSAGE } from '~/utils/constants';
import { exposeDebugInfo } from '~/utils/debug-info';
import { useInitRef } from '~/utils/init-ref';
import { createScopedLogger } from '~/utils/logger';
import { waitUntil, watchAll } from '~/utils/nanostore';
import { createSnapshot } from '~/utils/snapshots';
import { stripIndents } from '~/utils/stripIndent';

const logger = createScopedLogger('useMessages');

type StopReason = 'focused-file-error' | 'aborted-by-user';
export type SetMessages = (messages: Message[] | ((messages: Message[]) => Message[])) => void;

export interface MessagesOptions {
  resetInput?: () => void;
  previewRef: RefObject<PreviewRef>;
}

export function useMessages({ previewRef, resetInput }: MessagesOptions) {
  const currentProblems = useStore(errorHandler.currentProblems);

  const user = userStore.getUser();

  const isLoadingRef = useRef(false);
  const stopRef = useRef<() => void>();
  const messageTurnsSinceLastUpdateRef = useRef(0);

  const stopWithReason = useCallback((reason?: StopReason) => {
    stopRef.current?.();

    // if there's an abort reason, generate a handleError
    if (reason) {
      handleError({
        code: 'api-error',
        message: stopReasonToMessage(reason),
        isRetryable: true,
      });
    }

    if (reason === 'aborted-by-user') {
      setMessages((messages) => {
        const lastMessage = messages.at(-1);

        sendAnalyticsEvent({
          action: AnalyticsAction.Track,
          payload: {
            event: AnalyticsTrackEvent.ChatCancelled,
            properties: {
              messageId: lastMessage?.id,
            },
          },
        });

        // we need to find the id of the message that was cancelled
        let assistantMessageFound = false;
        let cancelledMessage: Message | undefined;

        for (let i = messages.length - 1; i >= 0; i--) {
          const message = messages[i];

          if (assistantMessageFound && message.role === 'user') {
            cancelledMessage = message;

            break;
          }

          if (message.role === 'assistant' && message.status === 'incomplete') {
            // this is the current message that is being streamed
            assistantMessageFound = true;
          }
        }

        if (cancelledMessage) {
          clientFetch('/api/chat/cancel', {
            method: 'POST',
            body: JSON.stringify({ projectId: projectStore.projectId.get(), message: cancelledMessage?.id }),
          });
        }

        return messages;
      });
    }
  }, []);

  const completedMessagesRef = useInitRef(() => new Set<string>());
  const completedMessages = completedMessagesRef.current;

  const diffParserRef = useRef<StreamingDiffParser | undefined>();

  const messageParser = useMemo(
    () =>
      new StreamingMessageParser({
        callbacks: {
          onArtifactOpen: (data) => {
            logger.trace('onArtifactOpen', data);

            /**
             * Use requestAnimationFrame to ensure the workbench is shown after the current frame is rendered.
             * This helps to avoid potential layout thrashing and ensures smooth UI updates as well as making
             * sure that the workbench is animated in if it's not already open.
             */
            requestAnimationFrame(() => {
              workbenchStore.showWorkbench.set(true);
            });

            const isFirstArtifact = !workbenchStore.firstArtifact;

            workbenchStore.addArtifact(data);

            if (isFirstArtifact && templateStore.template) {
              logger.debug('Create ImportTemplateAction');

              const importTemplateAction: ActionCallbackData = {
                artifactId: data.id,
                messageId: data.messageId,
                action: new ImportTemplateAction(data.id, data.messageId, `${data.id}-importTemplate`),
              };

              workbenchStore.addAction(importTemplateAction, true);
              workbenchStore.runAction(importTemplateAction);

              const installAction: ActionCallbackData = {
                artifactId: data.id,
                messageId: data.messageId,
                action: new InstallAction(data.id, data.messageId, `${data.id}-install`),
              };

              workbenchStore.addAction(installAction, true);
              workbenchStore.runAction(installAction);
            }
          },
          onArtifactClose: (data, shouldRun) => {
            logger.trace('onArtifactClose');

            workbenchStore.updateArtifact(data, { closed: true }, shouldRun);
          },
          onActionOpen: (data, shouldRun) => {
            logger.trace('onActionOpen', data.action);

            if (data.action.type === 'file' && !workbenchStore.isAIAllowedToEdit(data.action.filePath)) {
              stopWithReason('focused-file-error');
              data.action.abort();
            }

            // we only add shell actions when the close tag got parsed because only then we have the content
            if (data.action.type !== 'shell') {
              workbenchStore.addAction(data, shouldRun);
            }
          },
          onActionClose: (data, shouldRun) => {
            logger.trace('onActionClose', data.action);

            if (data.action.type === 'shell') {
              if (InstallAction.isSameAsInstallAction(data.action)) {
                data.action = new InstallAction(
                  data.artifactId,
                  data.messageId,
                  data.action.id,
                  false,
                  data.action.content.get(),
                );
              }

              workbenchStore.addAction(data, shouldRun);
            }

            if (shouldRun) {
              workbenchStore.runAction(data).catch((error: object) => {
                const chatError = ChatError.parse(
                  ChatError,
                  error,
                  'An unknown error occured while running an action.',
                );
                handleError(chatError);
              });
            }
          },
          onActionFix: (data) => {
            logger.warn(
              `Encountered invalid action type: '${data.type}'. If not undefined, replaced with: '${data.newType}'`,
            );

            sendAnalyticsEvent({
              action: AnalyticsAction.Track,
              payload: {
                event: AnalyticsTrackEvent.InvalidBoltActionType,
                properties: { actionType: data.type, newType: data.newType },
              },
            });
          },
          onFileStream: ({ action, data }) => {
            logger.trace('onFileStream', action, data);

            return onFileStream({ action, data }, { diffParserRef });
          },
        },
      }),
    [],
  );

  const handleError = useCallback((error: ChatError['data']) => {
    resetInput?.();

    if (error.code === 'free-user-rate-limited') {
      userStore.freeUserRateLimited.set(true);

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.BoltConcurrentFreeUsers,
        },
      });
    } else if (error.code === 'rate-limited') {
      userStore.rateLimitReached.set(true);
    }

    if (error.code === 'bad-server-response' && (error.status === 403 || error.status === 404)) {
      toast.error(
        'Users are reporting issues with Chrome Extensions and Antivirus software. If you continue to experince errors, please try disabling any extensions or antivirus and try again.',
      );
    }

    setMessages((messages) => {
      const newMessages = [...messages];

      const lastMessageIndex = newMessages.findLastIndex((message) => !message.ignore);
      const lastMessage = newMessages.at(lastMessageIndex);

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.ChatError,
          properties: {
            messageId: lastMessage?.id,
            ...error,
            reason: error.code,
            message: error.message,
            stack: error.stack,
          },
        },
      });

      // remove problems message and push into a new message
      if (lastMessage?.type === 'problems') {
        newMessages.splice(lastMessageIndex, 1);

        lastMessage.problems.forEach((problem) => {
          errorHandler.push(problem);
        });

        toast.error(error.message);
      }

      if (lastMessage?.type === 'chat') {
        if (lastMessage.role === 'user') {
          newMessages[lastMessageIndex] = {
            ...lastMessage,
            error,
            ignore: true,
            status: 'unsent',
          };
        } else {
          newMessages[lastMessageIndex] = {
            ...lastMessage,
            error,
            status: 'incomplete',
          };
        }
      }

      return newMessages;
    });
  }, []);

  const chat = useChat({
    api: '/api/chat',
    fetch: async (input, init) => {
      const response = await clientFetch(input, init);

      if (!user?.featureFlags.streamingWorker) {
        // if the user doesn't have the streaming worker feature flag enabled, we just return the response as is
        return response;
      }

      if (!response.ok || !response.body) {
        return response;
      }

      let offset = 0;

      const stream = new ReadableStream({
        start(controller) {
          let reader = response.body!.getReader();

          async function push(skipBytes = 0) {
            try {
              while (true) {
                const { done, value } = await reader.read();

                if (done) {
                  controller.close();
                  return;
                }

                let chunk = value;

                const diff = chunk.byteLength - skipBytes;

                if (diff <= 0) {
                  // we skip the bytes, but they are not enough to read the next chunk
                  skipBytes -= chunk.byteLength;

                  continue;
                } else if (diff < chunk.byteLength) {
                  // we skip the remaining bytes and only sent what we didn't receive yet
                  chunk = chunk.subarray(skipBytes);

                  skipBytes = 0;
                }

                offset += chunk.byteLength;

                controller.enqueue(chunk);
              }
            } catch (err: any) {
              const errorMessage = err.message;

              // the network error is different for every browser because it is thrown from inside the browser itself
              const isChromeNetworkError = errorMessage === 'network error';
              const isFirefoxNetworkError = errorMessage === 'error in input stream';
              const isSafariNetworkError = errorMessage === 'load failed';

              const isTypeError = err instanceof TypeError;

              if (isTypeError && (isChromeNetworkError || isFirefoxNetworkError || isSafariNetworkError)) {
                // we have to retry
                function retry(count = 1) {
                  const timeout = Math.min(count * 100, 1000);

                  setTimeout(async () => {
                    try {
                      const res = await clientFetch(input, init);

                      if (!res.ok || !res.body) {
                        // throw so we at least retry
                        throw new Error(`Failed to fetch: ${res.status} ${res.statusText}`);
                      }

                      sendAnalyticsEvent({
                        action: AnalyticsAction.Track,
                        payload: {
                          event: AnalyticsTrackEvent.NetworkFailureRecovery,
                          messageId: chatHooks.lastUserMessage.get()?.id,
                          properties: {
                            status: 'success',
                          },
                        },
                      });

                      reader = res.body.getReader();

                      push(offset);
                    } catch {
                      if (count >= 100) {
                        sendAnalyticsEvent({
                          action: AnalyticsAction.Track,
                          payload: {
                            event: AnalyticsTrackEvent.NetworkFailureRecovery,
                            messageId: chatHooks.lastUserMessage.get()?.id,
                            properties: {
                              status: 'failed',
                            },
                          },
                        });

                        controller.error(err);

                        return;
                      }

                      retry(count + 1);
                    }
                  }, timeout);
                }

                retry();

                return;
              }

              controller.error(err);
            }
          }

          push();
        },
      });

      return new Response(stream, response);
    },
    experimental_prepareRequestBody: prepareRequestBody,
    keepLastMessageOnError: true,

    /**
     * Setting maxSteps > 1 is required for the AI SDK to properly handle client-side tool calls
     * and submit the final result after tool execution completes.
     */
    maxSteps: 2,

    onToolCall: async ({ toolCall }) => {
      if (toolCall.toolName === 'getDeploymentStatus') {
        const deployStatus = workbenchStore.lastArtifact?.runner.getDeployStatus();

        if (!deployStatus) {
          return NO_DEPLOYMENT_STATUS_MESSAGE;
        }

        return deployStatus.catch(() => 'This string is ignored');
      }

      return undefined;
    },
    async onResponse(response) {
      if (response.ok) {
        userStore.freeUserRateLimited.set(false);
        chatStore.requestStatus.set('COMPLETE');

        const stats = response.headers.get('tokenStats');

        if (stats) {
          // defensively try to parse the header
          try {
            chatStore.tokenStats.set(JSON.parse(stats));
          } catch (error) {
            console.error(error);
          }
        }

        return;
      }

      const error = await ClientError.parse(ChatError, response);

      chatStore.requestStatus.set('ERROR');

      /**
       * AI SDK expects an object as the error, but if it's an Error
       * it will consume it, so throw empty object.
       */
      throw error;
    },
    onError(error) {
      logger.error(error);

      let unparsedError: object = error;

      const errorMessage = error.message?.toLowerCase() ?? '';

      if (errorMessage === 'overloaded') {
        unparsedError = {
          code: 'overloaded',
          message: 'Anthropic API is overloaded, please wait a bit and try again.',
          isRetryable: true,
        } satisfies ChatError['data'];
      }

      if (error.message.startsWith('Project size exceeded')) {
        unparsedError = {
          code: 'project-too-big',
          message: error.message,
          isRetryable: false,
        } satisfies ChatError['data'];
      }

      if (errorMessage === 'login required') {
        unparsedError = {
          code: 'login-required',
          message: 'User session expired. Please reload page and login to continue prompting.',
          isRetryable: false,
        } satisfies ChatError['data'];
      }

      // the network error is different for every browser because it is thrown from inside the browser itself
      const isChromeNetworkError = errorMessage === 'network error';
      const isFirefoxNetworkError = errorMessage === 'error in input stream';
      const isSafariNetworkError = errorMessage === 'load failed';

      const isTypeError = error instanceof TypeError;

      if (isTypeError && (isChromeNetworkError || isFirefoxNetworkError || isSafariNetworkError)) {
        unparsedError = {
          code: 'network-error',
          title: 'There seems to be a connection issue',
          message:
            'This can happen if your internet drops or if something goes wrong on our side. Check your internet connection and retry.',
          isRetryable: true,
        } satisfies ChatError['data'];
      }

      const chatError = ChatError.parse(ChatError, unparsedError, 'An error occured while streaming this message.');

      sendAppSignalError(chatError);

      handleError(chatError);
    },
    onFinish: ({ id }, args) => {
      const { finishReason, usage } = args;

      switch (finishReason) {
        // we simply ignore tool-calls here and do nothing
        case 'tool-calls': {
          return;
        }

        case 'content-filter': {
          handleError({
            code: 'content-filter',
            message: 'Safety filter activated. Try rewording your message.',
            isRetryable: false,
          });

          break;
        }

        case 'stop': {
          completedMessages.add(id);

          break;
        }

        /**
         * If we reach here it means we reached the maximum number of continuations.
         *
         * If that's the case we just mark the message as completed. This won't mean
         * that the AI generated a complete response so the code is probably still
         * broken.
         */
        case 'length': {
          completedMessages.add(id);

          // fallthrough is intentional here
        }

        default: {
          // we send an event with the usage to help understand how expensive this request was
          sendAnalyticsEvent({
            action: AnalyticsAction.Track,
            payload: {
              event: AnalyticsTrackEvent.MessageCompleteError,
              properties: {
                finishReason,
                usage,
                messageId: id,
              },
            },
          });

          sendAppSignalError(new Error(`onFinish triggered with reason: ${finishReason}`));
        }
      }

      setMessages((messages) => {
        projectStore.refreshContextSize({
          messages,
          kind: 'on-finish',
          count: usage.promptTokens,
        });

        return messages;
      });

      // fetch the latest limits
      clientFetch('/api/token-stats')
        .then((response) => response.json<APITokenStatsResponse>())
        .then(({ tokenStats, user }) => {
          chatStore.setTokenStats(tokenStats);
          userStore.setUser(user);
        })
        .catch((error) => {
          logger.error(error);

          toast.error('Failed to update token usage');
        });

      logger.debug('Finished streaming');

      chatStore.setSpecialTokenPromptVisible(false);
    },
  });

  const { stop, isLoading } = chat;

  stopRef.current = stop;

  const setMessages: SetMessages = (valueOrSetter) => {
    return chat.setMessages((messages) => {
      const value = typeof valueOrSetter === 'function' ? valueOrSetter(messages as any) : valueOrSetter;

      chatHooks.messages.set(value);

      return value;
    });
  };

  const checkMessagesSize = useCallback(async (message: Message) => {
    const chatMessages = await new Promise<Message[]>((resolve) =>
      setMessages((messages) => {
        resolve([...messages, message]);

        return messages;
      }),
    );

    return projectStore.refreshContextSize({ messages: chatMessages, kind: 'check' });
  }, []);

  const [
    // whether or not user confirmation is needed before sending the message
    showUserConfirmationDialog,
    checkUserAcknowledgeLargeContext,
    acknowledgeAndContinue,
    closeUserConfirmation,
  ] = useUserConfirmationFlow(checkMessagesSize);

  const reload = async (chatRequestOptions?: ChatRequestOptions) => {
    setMessages((prevMessages) => {
      const newMessages = [...prevMessages];

      for (let i = 0; i < prevMessages.length; i++) {
        const isLast = i === prevMessages.length - 1;
        const { type, status } = prevMessages[i];

        if (type === 'problems' && status !== 'complete' && !isLast) {
          newMessages[i] = {
            ...prevMessages[i],
            status: 'complete',
          };
        }
      }

      return newMessages;
    });

    messageTurnsSinceLastUpdateRef.current++;

    return chat.reload(chatRequestOptions);
  };

  const append = useCallback(async (message: Message, shouldReload = true) => {
    const proceed = await checkUserAcknowledgeLargeContext(message);

    if (!proceed) {
      return;
    }

    errorHandler.ignore();

    const inspectedElement = workbenchStore.inspectedElement.get();

    if (inspectedElement && message.type === 'chat' && message.role === 'user') {
      message.inspectedElement = inspectedElement;
      workbenchStore.setInspectedElement(null);
    }

    setMessages((messages) => {
      const newMessages = [...messages];

      trackResolvedErrors(messages);

      const lastMessage = newMessages.at(-1) as Message | undefined;

      /**
       * Remove duplicate messages, ie. when the last message errored,
       * it would reset the input back to the errored message content
       * and sending it again would result in a duplicate message.
       */
      if (
        message.type === 'chat' &&
        lastMessage?.type === 'chat' &&
        lastMessage.status !== 'complete' &&
        message.role === lastMessage.role &&
        message.content === lastMessage.content &&
        isEqual(message.experimental_attachments, lastMessage.experimental_attachments)
      ) {
        if (lastMessage.error) {
          delete lastMessage.error;

          // if we don't remove the ignore after removing the error, it gets filtered out in experimental_prepareRequestBody
          lastMessage.ignore = false;
        }

        return newMessages;
      }

      newMessages.push(message);

      return newMessages;
    });

    if (shouldReload) {
      reload();
    }
  }, []);

  const messages = useMemo(() => {
    const messagesWithTypes = chat.messages.map((message) => {
      if ('type' in message) {
        return message as Message;
      }

      const { role } = message;

      switch (role) {
        case 'user': {
          return {
            fileSelections: [],
            ...message,
            type: 'chat',
            role,
          } satisfies Partial<Message>;
        }
        case 'assistant': {
          return { ...message, type: 'chat', role } satisfies Partial<Message>;
        }
      }

      return { ...message, type: 'chat', role } satisfies Partial<Message>;
    });

    const lastUserMessage = messagesWithTypes.findLastIndex(({ role }) => role === 'user');

    const messages = messagesWithTypes.map((message, i): Message => {
      if (('error' in message && message.error) || ('status' in message && message.status === 'complete')) {
        return message;
      }

      const streaming = isLoading && i >= lastUserMessage;

      const nextMessage = messagesWithTypes?.[i + 1] as Message | undefined;

      const nextMessageIsResponse =
        nextMessage?.type === 'chat' && nextMessage.role === 'assistant' && !!nextMessage.createdAt;

      let completed = completedMessages.delete(message.id);

      // when there's multiple assistant messages
      completed ||= message.role === 'assistant' && nextMessageIsResponse;

      // when this message comes after the last user message, and there's also a completed one
      completed ||=
        i >= lastUserMessage && messagesWithTypes.slice(lastUserMessage).some(({ id }) => completedMessages.has(id));

      const status = completed ? 'complete' : streaming ? 'streaming' : 'incomplete';

      if (message.role === 'user') {
        const unsent = status === 'incomplete' && !nextMessageIsResponse;

        return {
          ...message,
          status: unsent ? 'unsent' : status,
        };
      }

      return { ...message, status } as Message;
    });

    // store the proper messages in the useChat hook
    Object.assign(chat.messages, messages);

    const hasSupabasePendingChanges = !!supabaseStore.firstItemInConfirmationQueue.get();

    if (currentProblems.length > 0 && !hasSupabasePendingChanges) {
      messages.push(errorHandler.getMessage());
    }

    return messages;
  }, [isLoading, chat.messages, currentProblems]);

  const loadPreviousMessages = useCallback(async () => {
    const projectId = projectStore.getProjectId();
    const hasMorePreviousMessages = chatStore.hasMorePreviousMessages.get();
    const isLoadingPreviousMessages = chatStore.isLoadingPreviousMessages.get();

    if (!projectId || !hasMorePreviousMessages || isLoadingPreviousMessages) {
      return;
    }

    chatStore.isLoadingPreviousMessages.set(true);

    try {
      // we can't use the first message because it might be a bolt/technical message
      const firstMessageWithOrder = messages.find((message) => message.messageOrder !== undefined);
      const lastMessageOrder = firstMessageWithOrder?.messageOrder;

      const messagesData = await getChatHistory(projectId, userStore.getActiveOrganization().get(), lastMessageOrder);

      if (!messagesData) {
        return;
      }

      chatStore.hasMorePreviousMessages.set(messagesData.hasMore);

      setMessages((prevMessages) => [...messagesData.messages, ...prevMessages]);
    } catch (err) {
      logger.error(err);
      toast.error('Failed to load previous messages');

      chatStore.hasMorePreviousMessages.set(false);

      return;
    } finally {
      chatStore.isLoadingPreviousMessages.set(false);
    }
  }, [messages]);

  // needs to execute on every render to get up to date functions
  useEffect(() => {
    chatHooks.setupHooks({
      setMessages,
      reload,
      checkUserAcknowledgeLargeContext,
    });
  });

  useEffect(() => {
    let connectMessageCounter = 0;

    const unsubscribe = supabaseStore.supabaseStatus.subscribe(async (status, prevStatus) => {
      if (
        (prevStatus === 'NOT_CONFIGURED' || prevStatus === 'CONNECTING_TO_EXISTING_DATABASE') &&
        status === 'CONNECTING'
      ) {
        const value = await waitUntil(
          supabaseStore.requiresConfirmation,
          (value) => value === 'idle' || value === 'accepted' || value === 'declined',
        );

        if (value === 'declined') {
          return;
        }

        setMessages((prevMessages) => {
          createSnapshot(prevMessages);

          const newMessages = [...prevMessages];

          const artifactId = `supabase-connect:${crypto.randomUUID()}`;

          let content;

          const hasEdgeFunctions = Object.keys(workbenchStore.edgeFunctionsFiles.get()).length > 0;

          if (prevStatus === 'CONNECTING_TO_EXISTING_DATABASE') {
            content = [
              `I'll connect with your existing database`,
              `<boltArtifact id="${artifactId}" showActionsOnInit="true" title="Connect to Supabase">`,
              `  <boltAction type="supabase-existing-database"></boltAction>`,
              ...(hasEdgeFunctions ? [`  <boltAction type="supabase-edge-functions"></boltAction>`] : []),
              `</boltArtifact>`,
              `\n`,
            ].join('\n');
          } else {
            content = [
              `I'll help you connect to Supabase.\n`,
              `<boltArtifact id="${artifactId}" showActionsOnInit="true" title="Connect to Supabase">`,
              `  <boltAction type="supabase-oauth"></boltAction>`,
              `  <boltAction type="supabase-create-project"></boltAction>`,
              `  <boltAction type="supabase-setup-auth"></boltAction>`,
              `  <boltAction type="supabase-create-database"></boltAction>`,
              ...(hasEdgeFunctions ? [`  <boltAction type="supabase-edge-functions"></boltAction>`] : []),
              `  <boltAction type="supabase-finalize"></boltAction>`,
              `</boltArtifact>`,
              `\n\nFirst, I'll need your permission to access your Supabase account. This is required so I can help set up and manage your database. Once you authorize access, I'll create a new project for you.\n\nThis can take up to 5 minutes.`,
            ].join('\n');
          }

          newMessages.push({
            id: `${BOLT_SUPABASE_CONNECT_MSG_PREFIX}-${connectMessageCounter++}`,
            type: 'chat',
            role: 'assistant',
            status: 'complete',
            content,
            allowUndo: false,
            ignore: true,
            merge: false,
            parts: [],
          });

          return newMessages;
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    return watchAll(supabaseStore.firstItemInConfirmationQueue, workbenchStore.isStreaming).subscribe(
      ([supabaseDBChanges, isStreaming]) => {
        if (isStreaming) {
          return;
        }

        if (supabaseDBChanges) {
          setMessages((messages) => {
            const supabaseMessage = fromDBConfirmationInfoToChatMessage(supabaseDBChanges);

            const newMessages = [...messages];

            if (supabaseDBChanges.type === 'migration') {
              const indexOfMessage = newMessages.findIndex((message) => {
                return (
                  message.type === 'supabase' &&
                  message.change.id === supabaseDBChanges.id &&
                  message.changeStatus === 'pending'
                );
              });

              // remove the previous message that was holding the pending migrations
              if (indexOfMessage !== -1) {
                newMessages.splice(indexOfMessage, 1);
              }

              newMessages.push(supabaseMessage);

              return newMessages;
            }

            if (supabaseDBChanges.type === 'migration-error') {
              const { changeId } = supabaseDBChanges;

              const index = newMessages.findIndex(
                (message) => message.type === 'supabase' && message.change.id === changeId,
              );

              // mark previous message as failed
              newMessages[index] = {
                ...(newMessages[index] as SupabaseDBChangeMessage),
                changeStatus: 'failed',
              } satisfies SupabaseDBChangeMessage;

              newMessages.push(fromDBConfirmationInfoToChatMessage(supabaseDBChanges));

              return newMessages;
            }

            newMessages.push(supabaseMessage);

            return newMessages;
          });
        } else {
          setMessages((messages) => {
            const index = messages.findIndex(
              (message) => message.type === 'supabase' && message.changeStatus === 'pending',
            );

            const message = messages[index] as SupabaseDBChangeMessage | undefined;

            if (!message || !supabaseStore.isDiscarded(message.change.id)) {
              // if we don't have a message, or the mssage is not discarded, we don't do anything
              return messages;
            }

            const newMessages = [...messages];

            const [supabaseMessage] = newMessages.splice(index, 1) as [SupabaseDBChangeMessage];

            newMessages.push({
              ...supabaseMessage,
              changeStatus: 'discarded' as const,
            });

            return newMessages;
          });
        }
      },
    );
  }, []);

  useEffect(() => {
    // actions to perform when streaming is finished
    if (!isLoading && workbenchStore.isStreaming.get() && chatStore.promptMode.get() === 'build') {
      previewRef.current?.reload();
    }

    isLoadingRef.current = isLoading;

    workbenchStore.isStreaming.set(isLoading);
  }, [isLoading]);

  useEffect(() => {
    return versionHistoryStore.isProcessingFiles.subscribe((isProcessing: boolean, prevIsProcessing?: boolean) => {
      if (prevIsProcessing && !isProcessing) {
        delay(() => {
          setMessages((messages) => {
            createSnapshot(messages);
            return messages;
          });
        }, 100);
      }
    });
  }, []);

  const reloadPreview = useCallback(() => {
    return previewRef.current?.reload();
  }, []);

  const updateSupabaseMessageStatus = useCallback(
    (changeId: string, changeStatus: SupabaseDBChangeMessage['changeStatus']) => {
      setMessages((messages) => {
        const newMessages = [...messages];

        const index = newMessages.findIndex((message) => message.type === 'supabase' && message.change.id === changeId);

        newMessages[index] = {
          ...(newMessages[index] as SupabaseDBChangeMessage),
          changeStatus,
        } satisfies SupabaseDBChangeMessage;

        return newMessages;
      });
    },
    [],
  );

  const acceptSupabaseChange = useCallback(async (changeId: string) => {
    const { success, change } = await supabaseStore.acceptChange(changeId);

    if (success && change.type === 'migration') {
      setMessages((messages) => {
        const newMessages = [...messages];

        const index = newMessages.findIndex((message) => message.type === 'supabase' && message.change.id === changeId);

        newMessages[index] = {
          ...(newMessages[index] as SupabaseDBChangeMessage),
          changeStatus: 'applied',
        } satisfies SupabaseDBChangeMessage;

        return newMessages;
      });
    } else if (success && change.type === 'migration-error') {
      setMessages((messages) => {
        const newMessages = [...messages];

        const index = newMessages.findIndex((message) => message.type === 'supabase' && message.change.id === changeId);

        const { message, query, filePath } = change.problems[0];

        newMessages[index] = {
          ...(newMessages[index] as SupabaseDBChangeMessage),
          ignore: false,
          content: stripIndents`
            The migration file at '${filePath}' failed to apply to the database.

            This is the current content of the migration file:

            \`\`\`
            ${query}
            \`\`\`

            Here's the error message:

            \`\`\`
            ${message}
            \`\`\`

            Please rewrite the entire migration file at '${filePath}' with equivalent SQL that fixes this error.

            The new migration should achieve the SAME database changes but avoid the error above. Make sure to include all necessary SQL statements.
          `,
          changeStatus: 'applied',
        } satisfies SupabaseDBChangeMessage;

        return newMessages;
      });

      await reload();
    }

    // we just always reload the preview if there is one
    reloadPreview();
  }, []);

  const discardSupabaseChange = useCallback(async (changeId: string, removeMessage = false) => {
    try {
      await supabaseStore.discardChanges(changeId);

      setMessages((messages) => {
        if (removeMessage) {
          return messages.filter((message) => !(message.type === 'supabase' && message.change.id === changeId));
        }

        const newMessages = [...messages];

        const index = newMessages.findIndex((message) => message.type === 'supabase' && message.change.id === changeId);

        newMessages[index] = {
          ...(newMessages[index] as SupabaseDBChangeMessage),
          changeStatus: 'discarded',
        } satisfies SupabaseDBChangeMessage;

        return newMessages;
      });
    } catch (error) {
      logger.error(error);

      updateSupabaseMessageStatus(changeId, 'failed');
    }
  }, []);

  const [parsedMessages, setParsedMessages] = useState<Message[]>([]);

  useLayoutEffect(() => {
    const parsedMessages: Message[] = [];

    /**
     * Here we parse all the messages and join multiple `assistant` messages together. This only needs to happen
     * if we have 2 or more `assistant` messages following each other. This is required to avoid having the
     * streaming message parser from having incomplete artifacts or action.
     */
    for (let index = 0; index < messages.length; index++) {
      const message = { ...messages[index] };

      if (message.type !== 'chat' || message.role !== 'assistant') {
        // if the message is not a `chat` message or not coming from the `assistant`, we can just push it
        parsedMessages.push(message);

        continue;
      }

      while (messages[index + 1]?.role === 'assistant') {
        const nextMessage = messages[index + 1];

        if (nextMessage.merge === false || message.id.startsWith(BOLT_ATTACHMENTS_PREFIX)) {
          // do not merge if the next message is explicitly marked as "do not merge"
          break;
        }

        // branch switch messages should not be merged either
        if (isGithubBranchSwitch(nextMessage)) {
          break;
        }

        // merge the content from the next `assistant` message
        message.content += nextMessage.content;
        message.status = nextMessage.status as 'streaming' | 'complete';

        // move to the next message, as we've merged them
        index++;
      }

      const runActions = !message.error && !message.skipAction;

      message.content = messageParser.parse(message.id, message.content, runActions);

      parsedMessages.push(message);
    }

    setParsedMessages(parsedMessages);
  }, [messages, isLoading]);

  exposeDebugInfo({
    get uiMessages() {
      return messages;
    },
    get chatMessages() {
      return chat.messages;
    },
    get parsedChatMessages() {
      return parsedMessages;
    },
  });

  return {
    ...chat,
    showUserConfirmationDialog,
    acknowledgeAndContinue,
    closeUserConfirmation,
    reload,
    stop: stopWithReason,
    append,
    acceptSupabaseChange,
    discardSupabaseChange,
    setMessages,
    reloadPreview,
    messages: parsedMessages,
    loadPreviousMessages,
  };
}

function stopReasonToMessage(reason: StopReason): string {
  if (reason === 'focused-file-error') {
    return `This message was terminated to preserve your tokens because Bolt attempted to write to restricted files. If this isn't what you intended, update your restricted files configuration to allow Bolt to write to that destination. If this is what you intended, please try again with a slightly modified prompt.`;
  } else {
    reason satisfies 'aborted-by-user';

    return 'You cancelled this message.';
  }
}
